import type { AdPerformanceDemoCopy } from "@/components/home/growth-features/CcAdPerformanceDemo";
import type { CampaignAutomationDemoCopy } from "@/components/home/growth-features/CcCampaignAutomationDemo";
import seoWorkflowSectionJson from "@/data/copy/seo-workflow-section.json";
import { createCopyGetter } from "@/lib/localize-copy";
import type { Lang } from "@/lib/types";

export type SeoWorkflowInsightSeverity = "critical" | "opportunity" | "good";

export type SeoWorkflowConnectIntegrationCopy = {
  id: string;
  name: string;
  logo?: string;
  status: string;
};

export type SeoWorkflowConnectCardCopy = {
  title: string;
  description: string;
  badge: string;
  analyzing: string;
  /** Integration search mockup (AI automation); omit for URL / Search Console demo. */
  searchPrompt?: string;
  demoUrl?: string;
  integrations?: SeoWorkflowConnectIntegrationCopy[];
  activeFlowsCount?: string;
  siteHealth: string;
  good: string;
  pagesIndexed: string;
  coreWebVitals: string;
  pass: string;
  connectedVia: string;
};

export type SeoWorkflowTrackKeywordCopy = {
  rank: number;
  keyword: string;
  trend: "up" | "down";
  change: number;
  sparkline: string;
};

export type SeoWorkflowTrackCardCopy = {
  title: string;
  description: string;
  badge: string;
  panelTitle: string;
  location: string;
  keywords: SeoWorkflowTrackKeywordCopy[];
};

export type SeoWorkflowInsightItemCopy = {
  severity: SeoWorkflowInsightSeverity;
  label: string;
  text: string;
};

export type SeoWorkflowInsightsCardCopy = {
  title: string;
  description: string;
  badge: string;
  panelTitle: string;
  pill: string;
  items: SeoWorkflowInsightItemCopy[];
};

export type SeoWorkflowScaleMetricCopy = {
  label: string;
  before: string;
  after: string;
};

export type SeoWorkflowScaleCardCopy = {
  title: string;
  description: string;
  badge: string;
  realtime: string;
  metrics: SeoWorkflowScaleMetricCopy[];
};

export type SeoWorkflowCampaignCardCopy = {
  title: string;
  description: string;
  badge: string;
  demo: CampaignAutomationDemoCopy;
};

export type SeoWorkflowAdPerformanceCardCopy = {
  title: string;
  description: string;
  badge: string;
  demo: AdPerformanceDemoCopy;
};

export type SeoWorkflowCardsCopy = {
  connect: SeoWorkflowConnectCardCopy;
  track: SeoWorkflowTrackCardCopy;
  insights: SeoWorkflowInsightsCardCopy;
  /** SEO service workflow demo (before/after metrics). */
  scale?: SeoWorkflowScaleCardCopy;
  /** SEO automation donut demo (AI automation workflow). */
  campaign?: SeoWorkflowCampaignCardCopy;
  /** Organic search metrics demo (optional fourth card). */
  adPerformance?: SeoWorkflowAdPerformanceCardCopy;
};

export type SeoWorkflowSectionCopy = {
  eyebrow: string;
  title: string;
  description: string;
  cards: SeoWorkflowCardsCopy;
};

const getLocalizedSeoWorkflowSectionCopy = createCopyGetter(seoWorkflowSectionJson);

export function getSeoWorkflowSectionCopy(lang: Lang): SeoWorkflowSectionCopy {
  return getLocalizedSeoWorkflowSectionCopy(lang) as unknown as SeoWorkflowSectionCopy;
}

/** English card copy for dev previews when no `cards` prop is passed. */
export const SEO_WORKFLOW_DEFAULT_CARDS: SeoWorkflowCardsCopy =
  getSeoWorkflowSectionCopy("en").cards;
